import React, { useContext } from "react";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import {
  Box,
  Button,
  ButtonBase,
  Container,
  Grid,
  Hidden,
  Link,
  Typography,
  useTheme
} from "@mui/material";
import Hero from "../../components/layout/Hero";
import {
  BCPSearch,
  BCPArrowRightFromBracket,
  BCPCheck,
  BCPArrowUpFromBracket,
  BCPUser,
  BCPUserGroup,
  BCPEnvelope
} from "../../components/icons/bcp/BCPIcons";
import HighlightedEvents from "../../components/utilities/HighlightedEvents";
import FeaturedEvents from "../../components/utilities/FeaturedEvents";
import PostGallery from "../../components/utilities/PostGallery";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import EventsPreview from "../../components/utilities/EventsPreview";
import FollowedOrganizations from "../../components/utilities/FollowedOrganizations";
import SponsorsBar from "../../components/utilities/SponsorsBar";
import { findGetParameter } from "../../components/utilities/UtilityFunctions";
import { useTranslation } from "react-i18next";
import { getLanguageCookie } from "../../language/LanguageFunctions";

const SYSTEM_IMAGES_CLOUDFRONT = getEnvironmentVariable(
  "SYSTEM_IMAGES_CLOUDFRONT"
);
const SUPPORTS_CMS = findGetParameter("SUPPORTS_CMS") === "true";
const getAppLinks = () => {
  let appLinks = {};
  if (
    getEnvironmentVariable("APP_LINKS") !== undefined &&
    getEnvironmentVariable("APP_LINKS") !== "NOT SET"
  ) {
    try {
      appLinks = JSON.parse(getEnvironmentVariable("APP_LINKS"));
    } catch (err) {}
  }
  return appLinks;
};

const forArray = ["players", "game stores", "tournament organizers", "you"];

export default function HomeTesting() {
  const authContext = useContext(AuthContext);

  const language = getLanguageCookie();
  const { t } = useTranslation("translation", {
    lng: language,
    keyPrefix: "pages.home"
  });

  const isLoggedIn = authContext?.authState?.isAuthenticated;
  const isSubscribed = authContext?.authState?.identity?.subscriber;

  const theme = useTheme();
  const mode = theme.palette.mode;

  return (
    <Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <Hero
          backgroundImage={`${SYSTEM_IMAGES_CLOUDFRONT}/images/header.jpg`}
          sxBackground={{
            height: ["auto", "60vh"],
            maxHeight: ["400px", "600px"],
            minHeight: "350px",
            color: "common.white"
          }}
        />
        <Grid
          container
          spacing={2}
          sx={{
            maxWidth: "lg",
            p: 4,
            zIndex: 1,
            position: "absolute",
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                flexDirection: "column",
                gap: 2,
                color: mode === "dark" ? "text.primary" : "common.white"
              }}
            >
              <Box
                sx={{
                  display: ["flex", "block"],
                  justifyContent: "center",
                  width: "100%"
                }}
              >
                <Hidden mdDown>
                  <img
                    src={`${getEnvironmentVariable(
                      "SYSTEM_IMAGES_CLOUDFRONT"
                    )}/images/BannerLogoDarkLarge.png`}
                    alt="logo"
                    style={{
                      maxHeight: "160px"
                    }}
                  />
                </Hidden>
                <Hidden mdUp>
                  <img
                    src={`${getEnvironmentVariable(
                      "SYSTEM_IMAGES_CLOUDFRONT"
                    )}/images/BannerLogoDarkLarge.png`}
                    alt="logo"
                    style={{
                      maxHeight: "90px"
                    }}
                  />
                </Hidden>
              </Box>

              <Typography
                variant="h3"
                gutterBottom
                sx={{ fontWeight: 700, mb: 2 }}
              >
                {t("heroSlogan")}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  mt: -1,
                  display: "flex",
                  justifyContent: "center",
                  gap: 1,
                  fontSize: "18px"
                }}
              >
                <BCPCheck /> {t("forPlayers")}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  mt: -1,
                  display: "flex",
                  justifyContent: "center",
                  gap: 1,
                  fontSize: "18px"
                }}
              >
                <BCPCheck /> {t("forGameStores")}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  mt: -1,
                  display: "flex",
                  justifyContent: "center",
                  gap: 1,
                  fontSize: "18px"
                }}
              >
                <BCPCheck /> {t("forTournamentOrganizers")}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  mt: -1,
                  display: "flex",
                  justifyContent: "center",
                  gap: 1,
                  fontSize: "18px"
                }}
              >
                <BCPCheck /> {t("forYou")}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid
                item
                xs={12}
                sx={{ display: "flex", justifyContent: "center", gap: 2 }}
              >
                {!isLoggedIn ? (
                  <Button
                    href="/login"
                    variant="contained"
                    color="secondary"
                    startIcon={<BCPArrowRightFromBracket />}
                  >
                    {t("login")}
                  </Button>
                ) : (
                  <Button
                    focusRipple
                    href="/subscription"
                    variant="contained"
                    color="success"
                  >
                    {t("subscribe")}
                  </Button>
                )}

                <Button
                  href="/play/events"
                  variant="contained"
                  color="primary"
                  startIcon={<BCPSearch />}
                >
                  {t("findAnEvent")}
                </Button>
              </Grid>
              {(getAppLinks().player?.apple ||
                getAppLinks()?.player?.google) && (
                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", justifyContent: "center", gap: 2 }}
                >
                  {getAppLinks()?.player?.apple && (
                    <ButtonBase
                      focusRipple
                      style={{
                        width: "150px"
                      }}
                      href={getAppLinks().player.apple}
                      target="_blank"
                      aria-label={`View app in Apple App Store.`}
                    >
                      <span
                        style={{
                          width: "150px",
                          height: "44px",
                          backgroundSize: "150px 44px",
                          backgroundRepeat: "no-repeat",
                          backgroundImage: `url(${SYSTEM_IMAGES_CLOUDFRONT}/system-images/apple.png)`
                        }}
                      />
                    </ButtonBase>
                  )}
                  {getAppLinks().player?.google && (
                    <ButtonBase
                      focusRipple
                      style={{
                        width: "150px"
                      }}
                      href={getAppLinks().player.google}
                      target="_blank"
                      aria-label={`View app in Google Play Store.`}
                    >
                      <span
                        style={{
                          width: "150px",
                          height: "44px",
                          backgroundSize: "150px 44px",
                          backgroundRepeat: "no-repeat",
                          backgroundImage: `url(${SYSTEM_IMAGES_CLOUDFRONT}/system-images/google.png)`
                        }}
                      />
                    </ButtonBase>
                  )}
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Box>

      <FeaturedEvents
        title={t("eventSpotlight")}
        titleProps={{
          sx: { fontWeight: 700, mt: 2 },
          variant: "h3"
        }}
        containerSx={{ p: 1 }}
      />

      {!isLoggedIn && <HighlightedEvents />}

      <SponsorsBar
        imageProps={{ style: { height: "120px" } }}
        titleProps={{
          sx: { fontWeight: 700, mt: 2 },
          variant: "h3"
        }}
      />

      {SUPPORTS_CMS && (
        <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Typography variant="h3" sx={{ fontWeight: 700 }} gutterBottom>
                {t("news")}
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ p: 1 }}>
              <PostGallery
                searchLimit={6}
                doPaging={false}
                loadMoreHref="/news"
              />
            </Grid>
          </Grid>
        </Container>
      )}

      {!isLoggedIn ? (
        <>
          <Hero
            backgroundImage={`${SYSTEM_IMAGES_CLOUDFRONT}/images/section1.jpg`}
            sxBackground={{
              height: ["unset", "70vh"],
              maxHeight: ["unset", "700px"],
              minHeight: "unset",
              color: "common.white"
            }}
            maxWidth="md"
          >
            <Grid container sx={{ p: 1 }}>
              <Grid item xs={12} md={6} lg={4} />

              <Grid item xs={12} md={6} lg={8}>
                <Typography variant="h1" align="left" color="inherit" paragraph>
                  {t("forPlayersTitle")}
                </Typography>
                <Typography
                  variant="body1"
                  align="left"
                  color="inherit"
                  paragraph
                >
                  {t("forPlayersDescription")}
                </Typography>
                <Typography
                  variant="body1"
                  align="left"
                  color="inherit"
                  paragraph
                >
                  <Link
                    href={isSubscribed ? "/account" : "/subscription/player"}
                    underline="none"
                  >
                    {t("subscribe")}
                  </Link>{" "}
                  {t("subscribeAndSeeText")}
                </Typography>

                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: ["center", "flex-start"],
                    gap: 2,
                    flexWrap: "wrap"
                  }}
                >
                  {!isSubscribed || !isLoggedIn ? (
                    <>
                      {!isLoggedIn && (
                        <Button
                          variant="contained"
                          color="primary"
                          href="/login?page=signup"
                          startIcon={<BCPUser />}
                        >
                          {t("signUp")}
                        </Button>
                      )}

                      {!isSubscribed && (
                        <Button
                          variant="outlined"
                          color="primary"
                          href="/subscription"
                          startIcon={<BCPArrowUpFromBracket />}
                        >
                          {t("subscribeToUpgrade")}
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      {getAppLinks().player?.apple && (
                        <ButtonBase
                          focusRipple
                          style={{
                            width: "150px"
                          }}
                          href={getAppLinks().player.apple}
                          target="_blank"
                          aria-label={`View app in Apple App Store.`}
                        >
                          <span
                            style={{
                              width: "150px",
                              height: "44px",
                              backgroundSize: "150px 44px",
                              backgroundRepeat: "no-repeat",
                              backgroundImage: `url(${SYSTEM_IMAGES_CLOUDFRONT}/system-images/apple.png)`
                            }}
                          />
                        </ButtonBase>
                      )}
                      {getAppLinks().player?.google && (
                        <ButtonBase
                          focusRipple
                          style={{
                            width: "150px"
                          }}
                          href={getAppLinks().player.google}
                          target="_blank"
                          aria-label={`View app in Google Play Store.`}
                        >
                          <span
                            style={{
                              width: "150px",
                              height: "44px",
                              backgroundSize: "150px 44px",
                              backgroundRepeat: "no-repeat",
                              backgroundImage: `url(${SYSTEM_IMAGES_CLOUDFRONT}/system-images/google.png)`
                            }}
                          />
                        </ButtonBase>
                      )}
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Hero>
          <Hero
            backgroundImage={`${SYSTEM_IMAGES_CLOUDFRONT}/images/section2.jpg`}
            sxBackground={{
              height: ["unset", "70vh"],
              maxHeight: ["unset", "700px"],
              minHeight: "unset",
              color: "common.white"
            }}
            maxWidth="md"
          >
            <Grid container sx={{ p: 1 }}>
              <Grid item xs={12} md={6} lg={8}>
                <Typography variant="h1" align="left" color="inherit" paragraph>
                  {t("forTournamentOrganizersTitle")}
                </Typography>
                <Typography
                  variant="body1"
                  align="left"
                  color="inherit"
                  paragraph
                >
                  {t("forTournamentOrganizersDescription")}
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: ["center", "flex-start"],
                    gap: 2,
                    flexWrap: "wrap"
                  }}
                >
                  <Button
                    variant="contained"
                    href="/organize/organization"
                    startIcon={<BCPUserGroup />}
                  >
                    {t("createAnOrganization")}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Hero>
          <Hero
            backgroundImage={`${SYSTEM_IMAGES_CLOUDFRONT}/images/section3.jpg`}
            sxBackground={{
              height: ["unset", "70vh"],
              maxHeight: ["unset", "700px"],
              minHeight: "unset",
              color: "common.white"
            }}
            maxWidth="md"
          >
            <Grid container sx={{ p: 1 }}>
              <Grid item xs={12} md={6} lg={4} />

              <Grid item xs={12} md={6} lg={8}>
                <Typography variant="h1" align="left" color="inherit" paragraph>
                  {t("forRetailersTitle")}
                </Typography>
                <Typography
                  variant="body1"
                  align="left"
                  color="inherit"
                  paragraph
                >
                  {t("forRetailersDescription")}{" "}
                  <Link
                    href="mailto:support@bestcoastpairings.com"
                    color="inherit"
                  >
                    support@bestcoastpairings.com
                  </Link>
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: ["center", "flex-start"],
                    gap: 2,
                    flexWrap: "wrap"
                  }}
                >
                  <Button
                    variant="contained"
                    href="/support"
                    startIcon={<BCPEnvelope />}
                  >
                    {t("contactUs")}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Hero>
        </>
      ) : (
        <Box sx={{ width: "100%" }}>
          <Container sx={{ p: 1, mb: 2 }} maxWidth="lg">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <EventsPreview />
              </Grid>

              <Grid item xs={12}>
                <FollowedOrganizations />
              </Grid>
            </Grid>
          </Container>
        </Box>
      )}
    </Box>
  );
}
